import { z } from 'zod';

/**
 * Scenario classification for AI tool-calling matching.
 * Determines which specialized prompt and tool subset to use.
 */
export type ReconciliationScenarioType = 'AP' | 'AR' | 'TRANSFER' | 'GENERIC';

export const RECONCILIATION_SCENARIO_VALUES: readonly ReconciliationScenarioType[] = [
  'AP',
  'AR',
  'TRANSFER',
  'GENERIC',
] as const;

/**
 * Candidate types the AI tool-matching agent can select.
 * Extends the existing AP types with AR_INVOICE and BANK_TRANSFER.
 */
export type AiToolCandidateType = 'AP_PAYMENT' | 'AP_BILL' | 'AR_INVOICE' | 'BANK_TRANSFER';

const AiToolCandidateTypeSchema = z.enum(['AP_PAYMENT', 'AP_BILL', 'AR_INVOICE', 'BANK_TRANSFER']);

/**
 * One selected match from AI tool-matching (used for N:1 / split scenarios).
 */
export const AiToolSelectedCandidateSchema = z.object({
  id: z.string(),
  type: AiToolCandidateTypeSchema,
});

export type AiToolSelectedCandidate = z.infer<typeof AiToolSelectedCandidateSchema>;

/**
 * Result from a single tool call executed by ReconciliationQueryExecutor.
 */
export interface AiToolCallResult {
  toolName: string;
  data: unknown[];
  count: number;
  truncated: boolean;
}

/**
 * Adjustment suggested by the AI tool-matching agent.
 */
export const AiToolAdjustmentSchema = z.object({
  type: z.string(),
  amount: z.number(),
  description: z.string(),
});

export type AiToolAdjustment = z.infer<typeof AiToolAdjustmentSchema>;

/**
 * Structured result from the AI tool-matching agent after multi-turn investigation.
 */
export const AiToolMatchingResultSchema = z.object({
  selectedCandidateId: z.string().nullable(),
  candidateType: AiToolCandidateTypeSchema.nullable(),
  /** Multiple matches for N:1 (e.g. consolidated reimbursements). Keeps singular fields for backward compatibility. */
  selectedCandidates: z.array(AiToolSelectedCandidateSchema).optional(),
  confidence: z.number().min(0).max(1),
  scenarioType: z
    .enum([
      'SIMPLE_1_TO_1',
      'MARKETPLACE_N_TO_1',
      'FUEL_CARD_N_TO_1',
      'INSTALLMENT_WITH_IOF',
      'WITHHOLDING',
      'LATE_PAYMENT_FEES',
      'PARTIAL_PAYMENT',
      'N_TO_N_COMPLEX',
      'AR_RECEIPT',
      'AR_EARLY_DISCOUNT',
      'AR_PARTIAL_RECEIPT',
      'INTERNAL_TRANSFER',
      'UNKNOWN',
    ])
    .nullable(),
  reasoning: z.string(),
  recommendation: z.enum(['AUTO_LINK', 'MANUAL_REVIEW', 'CREATE_NEW', 'SPLIT_TRANSACTION']),
  suggestedAdjustments: z.array(AiToolAdjustmentSchema),
});

export type AiToolMatchingResult = z.infer<typeof AiToolMatchingResultSchema>;

/**
 * Extended result from AiMatchingLoop with execution metadata.
 */
export interface AiToolMatchingLoopResult extends AiToolMatchingResult {
  toolCallsUsed: number;
  iterationsUsed: number;
  elapsedMs: number;
  /** Per-tool execution counts from the LLM tool-calling loop (when available). */
  toolCallsByName?: Record<string, number>;
}

/**
 * Counterparty context passed from deterministic identification to the AI agent.
 */
export interface AiToolCounterpartyContext {
  type: 'VENDOR' | 'CUSTOMER' | 'EMPLOYEE' | 'BANK' | 'BANK_ACCOUNT' | 'UNKNOWN';
  id: string | null;
  confidence: number;
  matchMethod: string;
  name?: string;
  document?: string;
  /** Additional candidates when multiple matches exist (name-based) */
  otherCandidates?: {
    type: string;
    id: string;
    score: number;
    name?: string;
  }[];
}

/**
 * Enriched metadata extracted from the raw provider payload (Kamino, Pluggy, OFX).
 * Only AI-relevant fields are included — internal IDs and redundant data are stripped.
 */
export interface TransactionMetadataContext {
  source: 'kamino' | 'pluggy' | 'ofx' | 'unknown';

  // Kamino fields
  invoiceNumber?: number;
  counterpartyDocument?: string;
  classification?: string;
  costCenter?: string;
  expectedAmount?: number;
  paidAmount?: number;
  dueDate?: string;
  interestAmount?: number;
  penaltyAmount?: number;
  discountAmount?: number;
  observation?: string;
  businessUnit?: string;

  // Pluggy / Open Finance fields
  category?: string;
  operationType?: string;
  merchantCnpj?: string;
  merchantName?: string;
  payerDocument?: string;
  receiverDocument?: string;
  payerName?: string;
  receiverName?: string;

  // OFX fields
  fitId?: string;
  refNumber?: string;
}

/**
 * Transaction context sent to the AI agent as the initial user message.
 */
export interface AiToolTransactionContext {
  transaction: {
    id: string;
    originType: 'BANK_TX' | 'CARD_ITEM';
    amount: number;
    direction: 'DEBIT' | 'CREDIT';
    postedAt: string;
    description: string;
    counterpartyName?: string;
    counterpartyDocument?: string;
    paymentMethod?: string;
    referenceCode?: string;
    transactionType?: string;
    bankAccountId?: string;
    metadata?: TransactionMetadataContext;
  };
  counterparty: AiToolCounterpartyContext;
  scenario: ReconciliationScenarioType;
}
